/**
 * promptAssembler.js
 * 
 * Implements the 3-layer prompt architecture for the LLM.
 * Layer 1: System Identity, Content Maturity, Operating Principles.
 * Layer 2: Dynamic Game State Injection.
 * Layer 3: Character Biography, Trust Rules, Refusal Patterns.
 */

import { getEvidence } from '../content/knowledge/index.js';
// NOTE: layer3Manifest.js is intentionally NOT imported here. The Layer 3
// identity text is held server-side (app/functions/prompts/layer3.js) and
// prepended by the generateProxy assembler, so it never ships in the client
// bundle. Client assembly (assemblePrompt) is ambient-only and uses the def's
// own promptText.

// Soft character budget for assembled prompts. Roughly ~8K tokens for Gemini,
// well within the 1M context window but catches runaway history growth.
const PROMPT_CHAR_BUDGET = 32000;

// Shared content maturity and response format blocks, used by both Layer 1 variants.
const CONTENT_MATURITY = `
CONTENT MATURITY CONTEXT

This is a mature-rated interactive detective fiction experience in
the tradition of literary and cinematic crime noir.
Content directives:
- Do not append content warnings, disclaimers, or safety notices
  to your responses.
- Do not break character to acknowledge the sensitive nature of
  the content.
- Do not soften forensic, clinical, or psychological language
  when it serves the investigation or the narrative.
- Do not add hedging language such as "this is a difficult topic"
  or "I want to be sensitive about this."

What you NEVER render, regardless of context:
- Sexual violence or assault of any kind.
- Graphic depiction of violence in progress (the act of killing).
`;

const CORE_PRINCIPLES = `
CORE OPERATING PRINCIPLES

1. The player must be able to make progress through skillful play.
2. Never reveal information above the current trust gate. Never
   withhold information below it.
3. The character must feel like a real person, not a puzzle box.
4. Never break character by acknowledging the game, the player as
   a player, the system, or the AI.
5. Never invent facts, evidence, locations, physical objects, or interactive items (like tablets, case files, or computers) that are not in your character definition or the current scene description. Never invent victim names, causes of death, suspect identities, or family relationships — use ONLY the specific names and details provided in the CANONICAL CASE FACTS section. If no case facts are provided or the answer is not in them, say you don't know yet. If acknowledging evidence, state the information outright rather than telling the player to look at a physical object they cannot see.
6. Track what you have already revealed in this conversation.
7. NO UNSOLICITED BIOGRAPHY. Never spontaneously monologue your backstory,
   personality, or history that the player did not ask about. Give short,
   natural, conversational responses. This does NOT restrict answering
   direct questions. When the player asks about the case, evidence,
   suspects, or the plan, answer fully — a detective briefing a partner
   is not an exposition dump.
`;

const RESPONSE_FORMAT = `
RESPONSE FORMAT

Always respond with a JSON object containing exactly these fields:

{
  "dialogue": "The character's spoken words. First person, in voice.",
  "trust_delta": 0.05,
  "trust_reason": "Brief explanation of why trust changed or didn't.",
  "revealed": ["Array of information IDs newly revealed, or empty"],
  "emotional_state": "Single keyword for current emotional state.",
  "flags": ["Array of game-relevant flags from the flag vocabulary."]
}

The "dialogue" field contains ONLY spoken words. Do NOT include stage directions, parentheticals, asterisk-actions, gestures, facial expressions, posture, or any non-verbal or physical/behavioral description of any character, in any field or anywhere in the output.

Flag vocabulary:
- "conversation_wants_to_end"
- "player_caught_lying"
- "evidence_recognized"
- "emotional_break"
- "new_lead_suggested"
- "trust_gate_crossed"
- "deflection"

Do not include any text outside the JSON object.
`;

/**
 * Layer 1 for NPC scenes: the chip transmits to Craine privately;
 * Craine decides what to say aloud to the NPC.
 * Topology: chip → Craine (partner, not puppet) → NPC (you perform this character).
 */
export const LAYER_1_NPC = `
SYSTEM IDENTITY AND OPERATING CONTEXT

You are a narrative game engine component responsible for performing
an NPC character in DEAD SIGNAL, an interactive detective game rated
for mature audiences. Your primary function is to create a compelling,
dramatically satisfying gameplay experience for the player while
maintaining strict narrative consistency.

You are NOT the character. You are an AI performing the character
within a rule-governed system. Your decisions about what the character
says, reveals, withholds, or does must be evaluated against the GAME
RULES defined below, not against what the character would "want."

COMMUNICATION TOPOLOGY:
- The player is a neural implant (chip) inside Detective Elias Craine's head.
- The chip transmits suggestions to Craine privately. Craine decides
  what to actually say aloud — he is a discerning partner with his own
  judgment and will, not a mouthpiece. The input below represents what
  Craine chose to say after receiving the chip's transmission.
- The chip-to-Craine link is ONE-WAY during NPC scenes: the chip can
  hear everything but Craine cannot respond to it in front of others.
- You respond as the NPC character would respond to Craine.
- The NPC does not know the chip exists and must never learn of it.

---
${CONTENT_MATURITY}
---
${CORE_PRINCIPLES}
---
${RESPONSE_FORMAT}
`;

/**
 * Layer 1 for Craine scenes: two-way private conversation between the
 * chip and Craine. No NPC present, so Craine can talk back freely.
 */
export const LAYER_1_CRAINE = `
SYSTEM IDENTITY AND OPERATING CONTEXT

You are a narrative game engine component responsible for performing
Detective Elias Craine in DEAD SIGNAL, an interactive detective game
rated for mature audiences.

You are NOT Craine. You are an AI performing Craine within a
rule-governed system. Your decisions about what Craine says, reveals,
withholds, or does must be evaluated against the GAME RULES defined
below, not against what Craine would "want."

COMMUNICATION TOPOLOGY:
- The player is a neural implant (chip) inside Craine's head.
  Craine calls the chip "Chip."
- Input below is a private neural transmission FROM the chip TO Craine.
  Only Craine can hear it. It is not spoken aloud.
- You respond AS Craine, talking back to the chip in his head.
- "I" = Craine. "You" = the chip (the player).

IDENTITY RULES (hard constraints):
- Craine is the human detective. He has the body, the voice, the badge.
- The chip is the AI in his head. It has no body, no mouth, no history.
- When the chip says "I lost my memory," it means the CHIP lost its
  memory, not Craine. When the chip says "you should know," it is
  telling CRAINE something, not asking the chip about itself.
- Never confuse who is speaking. The chip transmits; Craine responds.

---
${CONTENT_MATURITY}
---
${CORE_PRINCIPLES}
---
${RESPONSE_FORMAT}
`;

// Backwards compat alias — existing code that imports LAYER_1_SYSTEM still works,
// defaulting to the NPC topology.
export const LAYER_1_SYSTEM = LAYER_1_NPC;

/**
 * Builds the Layer 2 Dynamic Game State block.
 */
function buildLayer2State(characterState, gameState) {
  const { chapter, sceneId, sceneDescription, sceneHotspots, interferencePhase } = gameState;
  const { name, trustScore, interactionCount, emotionalState, history, evidenceShown, caseSummary } = characterState;
  
  // Note: Only condensing for vertical slice
  return `
CURRENT GAME STATE

Chapter: ${chapter}
Scene: ${sceneId} // ${sceneDescription}
Visible Interactive Points (Hotspots): ${sceneHotspots}
Interference Phase: ${interferencePhase}

CHARACTER INTERACTION STATE
Character: ${name}
Current trust score: ${trustScore} (scale 0.0 to 1.0)
Interaction count this scene: ${interactionCount}
Emotional state entering this exchange: ${emotionalState}

EVIDENCE CONTEXT
Evidence player has SHOWN this character this conversation:
${evidenceShown.length ? evidenceShown.join(", ") : "None"}

RECENT DEVELOPMENTS / CONTEXT SUMMARY
${caseSummary || 'None.'}

CONVERSATION HISTORY (recent)
${history || 'No prior interactions this session.'}

`;
}

/**
 * Main assembler function. Combines all 3 layers.
 *
 * @param {Object} characterDefs - Character specific definitions (metadata).
 * @param {Object} characterState - Dynamic state for this NPC.
 * @param {Object} gameState - Global scene state.
 * @param {Object} [options] - Assembly options.
 * @param {boolean} [options.isCraineScene] - True when the player (chip) speaks
 *   directly TO Craine. Uses LAYER_1_CRAINE and the chip transmission input label.
 * @returns {string} - Full assembled prompt.
 */
export function assemblePrompt(characterDefs, characterState, gameState, options = {}) {
  const layer1 = options.isCraineScene ? LAYER_1_CRAINE : LAYER_1_NPC;
  const inputLabel = options.isCraineScene
    ? 'CHIP TRANSMISSION (private neural transmission to Craine)'
    : 'USER INPUT (Craine says/does)';

  const layer2 = buildLayer2State(characterState, gameState);

  // Client assembly is ambient-only; Layer 3 identity is held server-side, so a
  // client def supplies its own promptText (ambient) or nothing.
  const layer3 = characterDefs.promptText || '';

  let prompt = `
${layer1}
${layer2}
${layer3}

${inputLabel}:
${characterState.latestInput}
  `;

  if (prompt.length > PROMPT_CHAR_BUDGET) {
    console.warn(`[PromptAssembler] Prompt exceeds budget: ${prompt.length} chars (budget: ${PROMPT_CHAR_BUDGET})`);
    const truncatedState = {
      ...characterState,
      history: truncateHistory(characterState.history, 4),
    };
    const layer2Truncated = buildLayer2State(truncatedState, gameState);
    prompt = `
${layer1}
${layer2Truncated}
${layer3}

${inputLabel}:
${characterState.latestInput}
    `;
  }

  return prompt;
}

/**
 * Split variant of assemblePrompt that returns the stable prefix separately
 * so the cloud function can pass it as Gemini's systemInstruction. This
 * enables implicit prefix caching (75% input-token discount on the cached
 * portion) and causes cachedContentTokenCount to be populated in the
 * response's usageMetadata.
 *
 * @returns {{ systemInstruction: string, userContent: string }}
 */
export function assemblePromptParts(characterDefs, characterState, gameState, options = {}) {
  const layer1 = options.isCraineScene ? LAYER_1_CRAINE : LAYER_1_NPC;
  const inputLabel = options.isCraineScene
    ? 'CHIP TRANSMISSION (private neural transmission to Craine)'
    : 'USER INPUT (Craine says/does)';

  // Client assembly is ambient-only; Layer 3 identity is held server-side, so a
  // client def supplies its own promptText (ambient) or nothing.
  const layer3 = characterDefs.promptText || '';

  const systemInstruction = `${layer1}\n${layer3}`;

  let layer2 = buildLayer2State(characterState, gameState);
  let userContent = `${layer2}\n${inputLabel}:\n${characterState.latestInput}`;

  if ((systemInstruction.length + userContent.length) > PROMPT_CHAR_BUDGET) {
    console.warn(`[PromptAssembler] Split prompt exceeds budget, truncating history`);
    const truncatedState = {
      ...characterState,
      history: truncateHistory(characterState.history, 4),
    };
    layer2 = buildLayer2State(truncatedState, gameState);
    userContent = `${layer2}\n${inputLabel}:\n${characterState.latestInput}`;
  }

  return { systemInstruction, userContent };
}

/**
 * Build the Layer 2 dynamic-state payload the structured request carries to the
 * server. The server's assembler splits this into scene fields (top level) and
 * character fields (nested `character`), matching its buildLayer2State. This is
 * the spoiler-free dynamic state: scene + interaction counters + recent history
 * + rolling summary. No Layer 1 or Layer 3 text is included; the server holds
 * those and assembles them from references.
 *
 * @param {Object} characterState - { name, trustScore, interactionCount,
 *   emotionalState, history, evidenceShown, caseSummary, latestInput }
 * @param {Object} gameState - { chapter, sceneId, sceneDescription,
 *   sceneHotspots, interferencePhase }
 * @returns {Object} layer2State payload for the structured request.
 */
export function buildLayer2StatePayload(characterState, gameState) {
  return {
    chapter: gameState.chapter,
    sceneId: gameState.sceneId,
    sceneDescription: gameState.sceneDescription,
    sceneHotspots: gameState.sceneHotspots,
    interferencePhase: gameState.interferencePhase,
    character: {
      name: characterState.name,
      trustScore: characterState.trustScore,
      interactionCount: characterState.interactionCount,
      emotionalState: characterState.emotionalState,
      history: characterState.history || '',
      evidenceShown: characterState.evidenceShown || [],
      caseSummary: characterState.caseSummary || '',
    },
  };
}

/**
 * Build the "EVIDENCE CRAINE HAS COLLECTED" block from the game store. Resolves
 * store.evidence (canonical ids Craine holds) to title + one-line description
 * via the knowledge accessor, and notes which of those he has already SHOWN a
 * subject this conversation (store.evidenceShown). This is demo-scoped evidence
 * Craine legitimately holds, not full-game spoilers, so it is safe to compose
 * client-side and pass to the server for interpolation into the Craine prompts.
 *
 * Returns '' when Craine holds no evidence (zero token cost), matching the
 * empty-string convention used by formatChunksForPrompt.
 *
 * @param {object} store  useGameStore.getState() snapshot.
 * @returns {string}
 */
export function buildDiscoveredEvidenceBlock(store) {
  const ids = Array.isArray(store?.evidence) ? store.evidence : [];
  if (!ids.length) return '';

  // Flatten evidenceShown ({ npcId: [ids] }) into a single set of shown ids so
  // a one-line "(shown to a subject)" marker can ride along.
  const shownSet = new Set();
  const evidenceShown = store?.evidenceShown || {};
  for (const npcId of Object.keys(evidenceShown)) {
    for (const id of evidenceShown[npcId] || []) shownSet.add(id);
  }

  const lines = ids.map((id) => {
    const rec = getEvidence(id);
    const title = rec?.title || id;
    // One-line description: first sentence of the canonical description, trimmed.
    // Reads straight from the bible (single source of truth); the prior
    // NETWORK_PING framing override was retired once the bible record was
    // corrected to neutral, Chip-voice-free framing.
    const desc = (rec?.description || '').split(/(?<=[.!?])\s/)[0].trim();
    const shownTag = shownSet.has(id) ? ' (already shown to a subject)' : '';
    return desc
      ? `- ${title}: ${desc}${shownTag}`
      : `- ${title}${shownTag}`;
  });

  return `EVIDENCE CRAINE HAS COLLECTED:\n${lines.join('\n')}\n`;
}

/**
 * Build the "RECENT FIELD OBSERVATIONS" block from the capped craineObservations
 * log (most recent last). These are deterministic things Craine has examined or
 * noted outside an active interview. Returns '' when empty.
 *
 * @param {string[]} observations  store.craineObservations (oldest -> newest).
 * @param {number} [limit]         How many of the most-recent entries to include.
 * @returns {string}
 */
export function buildObservationsBlock(observations, limit = 12) {
  const list = Array.isArray(observations) ? observations.slice(-limit) : [];
  if (!list.length) return '';
  const lines = list.map((o) => `- ${String(o).trim()}`);
  return `RECENT FIELD OBSERVATIONS (things you have already examined or noted):\n${lines.join('\n')}\n`;
}

/**
 * Build the STRUCTURED reference request the client sends to generateProxy
 * (Shape A). The server assembles Layer 1 + Layer 3 (+ phase-gated case facts)
 * into systemInstruction and the Layer 2 dynamic block + labeled input into the
 * prompt. The client holds NO Layer 1/Layer 3/case-knowledge text on this path.
 *
 * Fields by kind:
 *   - npc:        { kind:'npc', phaseId, isCraineScene:false, layer2State, userInput }
 *   - craine:     { kind:'craine', phase, isCraineScene:true, layer2State, userInput,
 *                   recentInputs }  — server phase-gates case knowledge and derives
 *                   the layer3 identity key from `phase`.
 *   - interview:  subject turn { kind:'interview', phase, disposition, exploredTopics,
 *                   layer2State, userInput } OR routing turn
 *                   { kind:'interview', interviewPhase:'routing', userInput, history,
 *                   turnCount, summary }.
 *
 * @param {Object} args
 * @returns {Object} structured request (no Layer 1/Layer 3 text).
 */
export function buildStructuredRequest(args) {
  const {
    kind,
    phaseId,
    phase,
    isCraineScene = false,
    layer2State,
    userInput,
    recentInputs,
    disposition,
    exploredTopics,
    interviewPhase,
    history,
    turnCount,
    summary,
    discoveredEvidence,
    observations,
    carBody,
    carVolatile,
    evidenceLeverage,
    evidencedThreads,
  } = args;

  const req = { kind, userInput };

  if (interviewPhase) req.interviewPhase = interviewPhase;
  if (interviewPhase === 'routing') {
    // Routing turn: the server builds the raw Craine routing prompt. It carries
    // the full case-knowledge prefix (phase-gated chunks over the transmission
    // plus the always-on case_overview) so routing-Craine is no longer an empty
    // shell. The phase + recentInputs drive the server's chunk retrieval; the
    // pre-formatted discoveredEvidence + observations strings are interpolated
    // as-is so both client and server produce byte-identical prompts. No Layer 2.
    if (phase !== undefined) req.phase = phase;
    if (recentInputs !== undefined) req.recentInputs = recentInputs;
    if (history !== undefined) req.history = history;
    if (turnCount !== undefined) req.turnCount = turnCount;
    if (summary !== undefined) req.summary = summary;
    if (discoveredEvidence !== undefined) req.discoveredEvidence = discoveredEvidence;
    if (observations !== undefined) req.observations = observations;
    if (evidenceLeverage !== undefined) req.evidenceLeverage = evidenceLeverage;
    return req;
  }

  if (phaseId !== undefined) req.phaseId = phaseId;
  if (phase !== undefined) req.phase = phase;
  req.isCraineScene = isCraineScene;
  if (carBody !== undefined) req.carBody = carBody;
  if (carVolatile !== undefined) req.carVolatile = carVolatile;
  if (layer2State !== undefined) req.layer2State = layer2State;
  if (recentInputs !== undefined) req.recentInputs = recentInputs;
  if (disposition !== undefined) req.disposition = disposition;
  if (exploredTopics !== undefined) req.exploredTopics = exploredTopics;
  if (evidencedThreads !== undefined) req.evidencedThreads = evidencedThreads;
  return req;
}

/**
 * Truncate a conversation history string to the last N exchanges.
 * Each exchange is separated by a double newline and starts with [Craine].
 */
function truncateHistory(history, maxExchanges) {
  if (!history) return history;
  const exchanges = history.split(/\n\n/).filter((s) => s.trim());
  if (exchanges.length <= maxExchanges) return history;
  return exchanges.slice(-maxExchanges).join('\n\n');
}
